import { HttpDataFileImportService } from './../../service/http-data-file-import.service';
import { Component, Input, OnInit } from '@angular/core';
import { HttpClient, HttpEventType, HttpResponse } from '@angular/common/http';
import { concatMap, filter, finalize } from 'rxjs/operators';
import { NzUploadFile, NzUploadXHRArgs } from 'ng-zorro-antd/upload';
import { DataFileImportDto, ResultListDto } from '../../service/dto';
import { AuthService } from '../../service/auth.service';
import { Observable, Subscription, timer } from 'rxjs';
import { OnTableChange, TableColumn } from '../../components/table/table.component';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { faCheck, faClockRotateLeft, faGear, faXmark, faArrowsTurnToDots, faCheckCircle, faFileShield } from '@fortawesome/free-solid-svg-icons';
import { formatEtaLabel, normalizeNumberLabel } from '../../shared/format-utils';

@Component({
	selector: 'app-import-list',
	templateUrl: './import-list.component.html',
	styleUrl: './import-list.component.scss',
	standalone: false
})
export class ImportListComponent implements OnInit {
	subscription = new Subscription();

	results?: ResultListDto<DataFileImportDto>;
	loading = false;
	uploadProgress?: number;
	faCheckCircle = faCheckCircle;
	faXmark = faXmark;
	faClockRotateLeft = faClockRotateLeft;
	faFileShield = faFileShield;
	faGear = faGear;
	faArrowsTurnToDots = faArrowsTurnToDots;

	columnRef: TableColumn[] = [
		{ title: "File Name", property: "name" },
		{ title: "Size", cellRef: 'cellRefSize', exportValue: item => this.formatSize(item.uploadSection?.fileSizeLabel) },
		{ title: "Uploaded By", property: "uploadSection.agent.username" },
		{ title: "MNO", property: "partner.name" },
		{ title: "Uploaded At", cellRef: 'cellRefCreatedAt' , exportValue: item => `${item.createdAt}`},
		{ title: "Method", property: "uploadSection.type" },

		{ title: "Step", cellRef: 'cellRefStep' , exportValue: item => `${item.status}`},
		{ title: "Progress", cellRef: 'cellRefProgress' , exportValue: item => `${item.status}`},
		{ title: "Total Records", cellRef: 'cellRefTotalRecords', exportValue: item => this.formatCount(item.uploadSection?.lineCount) },
		{ title: "Estimated Completion", cellRef: "cellRefCompletionEstimate" , exportValue: item => this.formatEta(item)},

		{ title: "Status", cellRef: "cellRefStatus", exportValue: item => `${item.status}`},
	];

	constructor(
		private service: HttpDataFileImportService,
		private authService: AuthService,
		private router: Router,
		private message: NzMessageService) { }

	ngOnInit(): void {
		this.subscription.add(timer(100, 5000)
			.pipe(
				filter(item => this.authService.isAuthenticated()),
				concatMap(result => this.service.getList(this.results?.pageNumber ?? 0, this.results?.pageLength ?? 10))
			)
			.subscribe(result => {
				this.results = result;
			})
		);
	}
	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
	doFetchResults(input: OnTableChange) {
		this.loading = true;
		this.service.getList(input.pageIndex, input.pageSize)
			.pipe(finalize(() => {
				this.loading = false;
			}))
			.subscribe(result => {
				this.results = result;
			});
	}


	beforeUpload = (file: NzUploadFile, fileList: NzUploadFile[]): boolean => {
		if (file.type === 'text/csv') {
			return true;
		}
		if (file.type === 'application/zip' || file.name.endsWith('.zip')) {
			return true;
		}
		this.message.warning('Invalid file format. Please upload a CSV or ZIP.');
		return false;
	}
	// kenya-restyle.md §7 "Duplicate rows" — the upload control stayed
	// interactive while a request was already in flight, so a second
	// click/drop before the first upload finished queued a second
	// submission of the same file. Guarding on uploadProgress (also
	// bound to [nzDisabled] on the nz-upload in the template) closes
	// that window.
	customUpload = (item: NzUploadXHRArgs): any => {
		if (this.uploadProgress !== undefined) {
			this.message.warning('An upload is already in progress.');
			return { abort() { } };
		}
		this.uploadProgress = 0;
		const subscription = this.service.uploadCsvFile(item.file as any)
			.pipe(finalize(() => this.uploadProgress = undefined))
			.subscribe({
				next: (event) => {
					if (event.type === HttpEventType.Response) {
						const body = event.body as DataFileImportDto[];
						this.uploadProgress = 100;
						this.message.info("Upload Done");
						this.doFetchResults({pageIndex: this.results?.pageNumber ?? 0, pageSize: this.results?.pageLength ?? 10, filters:[]});
					}
					if (event.type === HttpEventType.UploadProgress) {
						console.log(`Event Total: [${event.total} - Event Loaded [${event.loaded}]]`);
						const percentage = Math.round((100 * event.loaded) / (event.total ?? event.loaded));
						this.uploadProgress = percentage == 100 ? 99 : percentage;
					}
				},
				error: (err) => item.onError!(err, item.file!)
			});
		return { abort() { subscription.unsubscribe(); } };
	}
	open(input: DataFileImportDto) {
		this.router.navigate(['import', 'view', input.id]);
	}

	formatSize(label?: string): string {
		return normalizeNumberLabel(label);
	}
	formatCount(value?: number): string {
		return value == null ? '—' : normalizeNumberLabel(String(value));
	}
	// kenya-restyle.md §7 "Estimated completion" — the ETA column must
	// read the same per-status section the "Step" column already does
	// (row.importingSection while Importing, row.processingSection while
	// Processing); it was previously hardcoded to row.completionSection,
	// which only has real data once the job is fully done, so it showed
	// that section's leftover/defaulted 0%-progress estimate ("29773
	// days", "Infinite") for jobs still in progress.
	formatEta(row: DataFileImportDto): string {
		const section = row.status == 'Importing' ? row.importingSection : row.processingSection;
		return formatEtaLabel(section?.completionEstimateInLabel, section?.progressPercentage);
	}
}
